import React from 'react';
import { useDispatch } from 'react-redux';
import { UnitType } from '@src/core/enums';
import { ArcSocketContext, ChannelResponseEnum } from '@src/core/socket';
import { SettingUnitType } from '../entities';
import { baseSettingsApi, useGetSettingsQuery } from '../redux';
import {
  getUnitType,
  isUnitType,
  setUnitType,
  subscribeUnitType,
} from './unit-type-store';

const UnitTypeContext = React.createContext<UnitType>(UnitType.SI);

export function useUnitType(): UnitType {
  return React.useContext(UnitTypeContext);
}

export const UnitsProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { data } = useGetSettingsQuery();
  const { subscribe, unsubscribe } = React.useContext(ArcSocketContext);
  const dispatch = useDispatch();
  const [unitType, setUnitState] = React.useState<UnitType>(getUnitType);

  React.useEffect(() => subscribeUnitType(() => setUnitState(getUnitType())), []);

  // Live-sync the org display preference: the backend pushes an empty UPDATE_SETTINGS
  // signal when an admin changes units, so we refetch settings via tag invalidation.
  // getSettings' onQueryStarted then realigns the unit type and notifies all consumers.
  React.useEffect(() => {
    subscribe(ChannelResponseEnum.UPDATE_SETTINGS, () => {
      dispatch(baseSettingsApi.util.invalidateTags(['Settings']));
    });
    return () => unsubscribe(ChannelResponseEnum.UPDATE_SETTINGS);
  }, [dispatch, subscribe, unsubscribe]);

  React.useEffect(() => {
    const units = data?.items.find((item) => item.key === SettingUnitType.UNITS);
    const unitValue = units?.value;
    if (isUnitType(unitValue)) {
      setUnitType(unitValue);
    }
  }, [data]);

  return (
    <UnitTypeContext.Provider value={unitType}>
      {children}
    </UnitTypeContext.Provider>
  );
};
